import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';

export default function Home() {
  const { user, profile } = useAuth();
  const navigate = useNavigate();
  
  const isVerified = profile?.account_type === 'verified_member' || profile?.account_type === 'moderator';
  const ctaLink = user ? (isVerified ? '/forums' : '/profile') : '/register';
  const ctaText = user ? (isVerified ? 'Go to Forums' : 'Complete Verification') : 'Join the Community';

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url('https://public.readdy.ai/ai/img_res/c71f5766ae5296cd00e163d2a8c9d114.jpg')`
          }}
        ></div>
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/40"></div>
        
        <div className="relative z-10 max-w-4xl mx-auto px-6 text-center text-white">
          <h1 className="text-5xl md:text-6xl font-bold mb-6 leading-tight">
            Welcome to Lone Pine Public Square
          </h1>
          <p className="text-xl md:text-2xl mb-8 text-gray-100 leading-relaxed">
            Your voice matters. Join transparent community discussions, share documents, and participate in HOA governance.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link 
              to={ctaLink}
              className="px-8 py-4 bg-teal-600 hover:bg-teal-700 text-white font-semibold rounded-lg transition-colors whitespace-nowrap"
            >
              {ctaText}
            </Link>
            <Link 
              to="/forums"
              className="px-8 py-4 bg-white/10 hover:bg-white/20 backdrop-blur-sm text-white font-semibold rounded-lg border-2 border-white/30 transition-colors whitespace-nowrap"
            >
              Explore Forums
            </Link>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Platform Features</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Everything you need for transparent community governance and meaningful member engagement
            </p>
          </div>

          {/* Features Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
            <div className="bg-white rounded-lg p-8 hover:shadow-lg transition-shadow">
              <div className="w-14 h-14 bg-blue-50 rounded-lg flex items-center justify-center mb-6">
                <i className="ri-chat-3-line text-2xl text-blue-600"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Discussion Forums</h3>
              <p className="text-gray-600 mb-4">
                Engage in meaningful conversations about community matters, share ideas, and collaborate on solutions.
              </p>
              <a
                href="/forums"
                className="text-teal-600 font-semibold hover:text-teal-700 transition-colors"
              >
                Join Discussion →
              </a>
            </div>

            <div className="bg-white rounded-lg p-8 hover:shadow-lg transition-shadow">
              <div className="w-14 h-14 bg-purple-50 rounded-lg flex items-center justify-center mb-6">
                <i className="ri-bar-chart-box-line text-2xl text-purple-600"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Polls &amp; Voting</h3>
              <p className="text-gray-600 mb-4">
                Participate in community polls and vote on important decisions that shape our neighborhood.
              </p>
              <a
                href="/polls"
                className="text-teal-600 font-semibold hover:text-teal-700 transition-colors"
              >
                View Polls →
              </a>
            </div>

            <div className="bg-white rounded-lg p-8 hover:shadow-lg transition-shadow">
              <div className="w-14 h-14 bg-green-50 rounded-lg flex items-center justify-center mb-6">
                <i className="ri-file-text-line text-2xl text-green-600"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Document Library</h3>
              <p className="text-gray-600 mb-4">
                Access important HOA documents, meeting minutes, financial reports, and community resources.
              </p>
              <a
                href="/documents"
                className="text-teal-600 font-semibold hover:text-teal-700 transition-colors"
              >
                Browse Documents →
              </a>
            </div>

            <div className="bg-gray-50 rounded-lg p-8 hover:shadow-lg transition-shadow">
              <div className="w-14 h-14 bg-teal-500 rounded-lg flex items-center justify-center mb-6">
                <i className="ri-shield-check-line text-2xl text-white"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Moderated Platform</h3>
              <p className="text-gray-600 leading-relaxed">
                Fair moderation that protects free speech while maintaining respectful community standards
              </p>
            </div>

            <div className="bg-gray-50 rounded-lg p-8 hover:shadow-lg transition-shadow">
              <div className="w-14 h-14 bg-teal-500 rounded-lg flex items-center justify-center mb-6">
                <i className="ri-user-voice-line text-2xl text-white"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Anonymous Options</h3>
              <p className="text-gray-600 leading-relaxed">
                Post anonymously while maintaining accountability through verified member status
              </p>
            </div>

            <div
              onClick={() => navigate('/events')}
              className="bg-white p-8 rounded-lg shadow-sm hover:shadow-md transition-shadow cursor-pointer"
            >
              <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-calendar-event-line text-2xl text-purple-600"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Events Calendar</h3>
              <p className="text-gray-600">
                Stay updated with HOA meetings, community events, and activities
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Categories Section */}
      <section className="py-20 px-4 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Discussion Categories</h2>
            <p className="text-lg text-gray-600">
              Organized forums for every aspect of community life
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: 'ri-government-line', name: 'Governance', desc: 'Board decisions and HOA policies' },
              { icon: 'ri-money-dollar-circle-line', name: 'Financial', desc: 'Budgets and assessments' },
              { icon: 'ri-home-4-line', name: 'STR Issues', desc: 'Short-term rental discussions' },
              { icon: 'ri-road-map-line', name: 'Roads', desc: 'Maintenance and infrastructure' },
              { icon: 'ri-team-line', name: 'Community', desc: 'Events and neighborhood topics' },
              { icon: 'ri-scales-3-line', name: 'Legal', desc: 'Legal matters and compliance' },
              { icon: 'ri-feedback-line', name: 'Feedback', desc: 'Suggestions and improvements' },
              { icon: 'ri-notification-3-line', name: 'Announcements', desc: 'Official HOA updates' }
            ].map((category, index) => (
              <Link 
                key={index}
                to="/forums"
                className="bg-white rounded-lg p-6 hover:shadow-md transition-shadow cursor-pointer group"
              >
                <div className="w-12 h-12 bg-teal-50 rounded-lg flex items-center justify-center mb-4 group-hover:bg-teal-100 transition-colors">
                  <i className={`${category.icon} text-xl text-teal-600`}></i>
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">{category.name}</h3>
                <p className="text-sm text-gray-600">{category.desc}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">How It Works</h2>
            <p className="text-lg text-gray-600">
              Getting started is simple and secure
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-12">
            <div className="text-center">
              <div className="w-16 h-16 bg-teal-500 rounded-full flex items-center justify-center mx-auto mb-6 text-white text-2xl font-bold">
                1
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Register & Verify</h3>
              <p className="text-gray-600 leading-relaxed">
                Create your account with your email and property address. Our verification process ensures only HOA members participate
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-teal-500 rounded-full flex items-center justify-center mx-auto mb-6 text-white text-2xl font-bold">
                2
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Engage & Discuss</h3>
              <p className="text-gray-600 leading-relaxed">
                Post discussions, share documents, vote in polls, and comment on topics that matter to you and the community
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-teal-500 rounded-full flex items-center justify-center mx-auto mb-6 text-white text-2xl font-bold">
                3
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Shape Our Future</h3>
              <p className="text-gray-600 leading-relaxed">
                Your voice contributes to transparent governance and helps build a stronger, more connected community
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-teal-600 to-teal-700">
        <div className="max-w-4xl mx-auto px-6 text-center text-white">
          <h2 className="text-4xl font-bold mb-6">Ready to Make Your Voice Heard?</h2>
          <p className="text-xl mb-8 text-teal-50">
            Join your neighbors in building a more transparent and engaged community.
          </p>
          <Link 
            to={ctaLink}
            className="inline-block px-8 py-4 bg-white text-teal-600 hover:bg-gray-100 font-semibold rounded-lg transition-colors whitespace-nowrap"
          >
            {ctaText}
          </Link>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-20 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-4 gap-8 text-center">
            <div>
              <div className="text-4xl font-bold text-teal-600 mb-2">33</div>
              <div className="text-gray-600">Member Properties</div>
            </div>
            <div>
              <div className="text-4xl font-bold text-teal-600 mb-2">8</div>
              <div className="text-gray-600">Discussion Categories</div>
            </div>
            <div>
              <div className="text-4xl font-bold text-teal-600 mb-2">100%</div>
              <div className="text-gray-600">Transparent Moderation</div>
            </div>
            <div>
              <div className="text-4xl font-bold text-teal-600 mb-2">24/7</div>
              <div className="text-gray-600">Platform Access</div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
